import { useEffect, useMemo, useRef, useState } from "react";

import { SearchIcon } from "@/assets/icons";
import LoadingWrapper from "@/components/ui/LoadingWrapper";
import { useIntersectionObserver } from "@/hooks/ui/useIntersectionObserver";
import { FilterOption } from "@/types/dataset";

import { twMerge } from "tailwind-merge";

const TEST_ID_PREFIX = "multiple-selection-attribute-filter";

type MultipleSelectionFilterProps = {
  options: FilterOption[];
  selectedOptions: string[];
  searchTerm: string;
  onSearchChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onSelectOption: (optionValue: string) => void;
  onSelectAll: (data: FilterOption[]) => void;
  isEdit?: boolean;
  isLoading?: boolean;
};

// Content limit for displaying options
const CONTENT_LIMIT = 100;

const MultipleSelectionFilter = ({
  options,
  selectedOptions,
  searchTerm,
  onSearchChange,
  onSelectOption,
  onSelectAll,
  isEdit = false,
  isLoading = false
}: MultipleSelectionFilterProps) => {
  const [currentLimit, setCurrentLimit] = useState(CONTENT_LIMIT);
  const isSelectedAll = useMemo(
    () => selectedOptions.length === options.length,
    [selectedOptions.length, options.length]
  );

  // Filter options based on search term
  // useMemo is used to optimize performance by memoizing the filtered options
  const filteredOptions = useMemo(
    () =>
      options
        .filter((option) =>
          option.label.toLowerCase().includes(searchTerm.toLowerCase())
        )
        .slice(0, currentLimit), // set a limit to the number of options displayed for performance
    [options, searchTerm, currentLimit]
  );

  useEffect(() => {
    // reset the limit when options or searchTerm change
    setCurrentLimit(CONTENT_LIMIT);
  }, [options, searchTerm]);

  const lastItemRef = useRef<HTMLLabelElement | null>(null);
  const isIntersecting = useIntersectionObserver(lastItemRef, {
    threshold: 0.5,
    rootMargin: "10px"
  });

  useEffect(() => {
    if (isIntersecting && currentLimit < options.length) {
      setCurrentLimit((prev) => prev + CONTENT_LIMIT);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isIntersecting, options]);

  return (
    <LoadingWrapper isLoading={isLoading} loadingClassName="h-full">
      <div className="flex flex-col flex-1 min-h-0 mt-3 font-sans">
        <div className="space-y-3 flex-none">
          <label
            className="text-sm text-txtBody font-semibold"
            htmlFor="search"
          >
            Options
          </label>
          <div className="relative">
            <input
              name="search"
              type="text"
              className="w-full h-[40px] pl-9 pr-3 border border-gainsboro rounded-xl text-sm"
              placeholder="Search"
              value={searchTerm}
              onChange={onSearchChange}
              data-testid={`${TEST_ID_PREFIX}-search-input`}
            />
            <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-txtPlaceholder" />
          </div>
        </div>

        <p className="text-xs text-txtBody mt-3">
          Selected options ({selectedOptions.length} of {options.length})
        </p>

        <div className="flex flex-col flex-1 min-h-0 mt-3">
          {options.length > 0 && (
            <label className="flex items-center gap-2 px-2 py-1 hover:bg-gray-50 cursor-pointer flex-none border-b border-gainsboro pb-2 mb-2">
              <input
                type="checkbox"
                className="rounded border-gray-300"
                checked={isSelectedAll}
                onChange={() => onSelectAll(isSelectedAll ? [] : options)}
              />
              <span className="text-sm text-txtBody">Select All</span>
            </label>
          )}
          <div
            className={twMerge(
              "overflow-auto flex-1 min-h-0",
              isEdit && "max-h-48"
            )}
          >
            {filteredOptions.length > 0 ? (
              <>
                {filteredOptions.map((option, index) => (
                  <label
                    key={option.id}
                    ref={
                      index === filteredOptions.length - 1 ? lastItemRef : null
                    }
                    className="flex items-center gap-2 px-2 py-1 hover:bg-gray-50 cursor-pointer"
                  >
                    <input
                      type="checkbox"
                      className="rounded border-gray-300"
                      checked={selectedOptions.includes(option.value ?? "")}
                      onChange={() =>
                        option.value && onSelectOption(option.value)
                      }
                    />
                    <span className="text-sm text-txtBody">{option.label}</span>
                  </label>
                ))}
              </>
            ) : (
              <div className="flex flex-col items-center justify-center h-full text-center p-4">
                <SearchIcon className="w-6 h-6 fill-txtPlaceholder mb-2" />

                <div className="text-txtPlaceholder mb-2 font-semibold">
                  Options Not Found
                </div>
                <div className="text-sm text-txtPlaceholder">
                  Please try again with different keywords.
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </LoadingWrapper>
  );
};

export default MultipleSelectionFilter;
